/* istanbul ignore file */
import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Container from 'reactstrap/lib/Container';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { FeatureSpecsEntities, FeatureSpecsPaths, VehicleEntities, VehicleModel } from 'client/data/models/vehicle-v2';
import { VisitorEntities, VisitorModel } from 'client/data/models/visitor';
import { FeaturesSpecsParams } from 'site-modules/shared/utils/features-specs/params';
import { LEAD_FORMS_CREATIVE_ID } from 'site-modules/shared/components/lead-form/unified-lead-form/constants/creative-ids';
import { getLeadFormCreativeId } from 'site-modules/shared/components/lead-form/utils/lead-form-creative-id';
import { MoreAboutMMYLink } from 'site-modules/shared/components/more-about-mmy-link/more-about-mmy-link';
import { BrandHero } from 'site-modules/shared/components/brand-hero/brand-hero';
import { FeaturesSpecsTable } from 'site-modules/features-specs/components/features-specs-table/features-specs-table.plat-4078-chal1';
import { InventoryButton } from 'site-modules/shared/components/inventory-button/inventory-button';
import { FeaturesSpecsCta } from 'site-modules/features-specs/components/features-specs-cta/features-specs-cta.plat-4078-chal4';
import { FeaturesTrimBox } from 'site-modules/features-specs/components/features-trim-box/features-trim-box.plat-4078-chal4';

import './style-features-specs.scss';

function StyleFeaturesSpecsUI({
  vehicle,
  styleId,
  styleOverview,
  popularStyles,
  vehicleName,
  params,
  visitorLocation,
  onStyleChange,
  pageName,
  isMobile,
}) {
  if (!styleOverview) {
    return null;
  }
  const { isUsed } = params;

  const currentStyle = popularStyles.find(({ id }) => id === styleId);

  return (
    <div className="style-features-specs pos-r">
      <BrandHero contentClassName="pt-0_75 pt-md-1_5" isMobile={isMobile}>
        <Row>
          <Col xs={12} md={9} className="px-0">
            <div className="header-container px-0_5 px-md-0">
              <h1
                className={classnames('title fw-bold text-white mb-0_25 mb-md-0_5', {
                  'size-30': isMobile,
                  'size-36': !isMobile,
                })}
              >
                {vehicleName} - Specs & Features
              </h1>
              <div>
                <MoreAboutMMYLink
                  vehicle={vehicle}
                  className="text-white"
                  linkClassName={null}
                  iconClassName={null}
                  linkToReview={isUsed}
                  showBuyOnlineBtn={isUsed}
                  isMobile={isMobile}
                  params={params}
                />
              </div>
            </div>
          </Col>
        </Row>
      </BrandHero>

      <Container className="style-features-container">
        <Row className="mb-2">
          <Col xs={12} lg={7}>
            <FeaturesTrimBox
              className="mb-2"
              params={params}
              styleOverview={styleOverview}
              popularStyles={popularStyles}
              onStyleChange={onStyleChange}
            />
            {params.isUsed ? (
              <div className="d-flex">
                <InventoryButton
                  params={params}
                  makeModelSubmodelYear={vehicle}
                  visitorLocation={visitorLocation}
                  className="fw-medium"
                  btnColor="blue-50"
                />
                <InventoryButton
                  params={params}
                  makeModelSubmodelYear={vehicle}
                  visitorLocation={visitorLocation}
                  className="fw-medium"
                  btnColor="blue-50"
                  isBuyOnline
                />
              </div>
            ) : (
              <FeaturesSpecsCta
                vehicle={vehicle}
                styleId={styleId}
                trimName={currentStyle?.trim}
                leadFormCreativeId={getLeadFormCreativeId({
                  isNewVin: true,
                  pageNameLegacy: pageName,
                  formName: LEAD_FORMS_CREATIVE_ID.FEATURE_SPECS,
                })}
                dataTrackingParent="features-specs-cta1"
              />
            )}
          </Col>
        </Row>

        <FeaturesSpecsTable
          styleId={styleId}
          visitorLocation={visitorLocation}
          vehicle={vehicle}
          trimName={currentStyle?.trim}
          params={params}
          isMobile={isMobile}
          pageName={pageName}
        />
      </Container>
    </div>
  );
}

StyleFeaturesSpecsUI.propTypes = {
  vehicle: VehicleEntities.MakeModelSubmodelYear.isRequired,
  popularStyles: VehicleEntities.PopularStyles.isRequired,
  params: FeaturesSpecsParams.isRequired,
  vehicleName: PropTypes.string.isRequired,
  styleId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  styleOverview: FeatureSpecsEntities.FeatureStyleOverviewEntity,
  visitorLocation: VisitorEntities.Location,
  onStyleChange: PropTypes.func,
  pageName: PropTypes.string,
  isMobile: PropTypes.bool,
};

StyleFeaturesSpecsUI.defaultProps = {
  styleId: undefined,
  styleOverview: null,
  visitorLocation: null,
  onStyleChange: undefined,
  pageName: null,
  isMobile: false,
};

export const propsAreEqual = (prevProps, { styleOverview }) => !styleOverview;
export const StyleFeaturesSpecs = connectToModel(React.memo(StyleFeaturesSpecsUI, propsAreEqual), {
  styleOverview: bindToPath(props => {
    const { styleId } = props; // user selection || page url || default style || undefined if parent component has not resolved popular styles yet
    if (styleId) {
      return FeatureSpecsPaths.buildStyleFeaturesOverviewPath(styleId);
    }
    /**
     *  we could return null instead of buildFeaturesOverviewPathForDefaultStyle(props.params)
     *  and component would work the same given the parent component resolves and passes the default styleId
     *  reason for returning buildFeaturesOverviewPathForDefaultStyle(props.params), however, is that
     *  1. it turns this into a standalone component that can be shared between pages
     *  2. would allow us to remove the complexity from above component, making the page simpler
     *  3. That path is in preload, having something in preload and not in any component would lead to confusion
     */
    return FeatureSpecsPaths.buildFeaturesOverviewPathForDefaultStyle(props.params);
  }, VehicleModel),
  visitorLocation: bindToPath('location', VisitorModel),
});
